import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { api } from '../lib/api-client';
import { syncFireAndForget } from '../lib/sync';

export type CoachGender = 'female' | 'male' | 'nb';
export type CoachTone = 'dominant' | 'playful' | 'sensual';

export interface CoachState {
  gender: CoachGender | null;
  tone: CoachTone | null;
  name: string | null;
  avatarUrl: string | null;
  dailyCommand: string | null;
  isConfigured: boolean;

  configure: (input: {
    gender: CoachGender;
    tone: CoachTone;
    name: string;
    avatarUrl: string;
    dailyCommand: string;
  }) => void;
  setDailyCommand: (command: string) => void;
  /** Per-field setters used by the Your-personality screen for live editing. */
  setName: (name: string) => void;
  setGender: (gender: CoachGender) => void;
  setTone: (tone: CoachTone) => void;
  /** Overwrite local state with the server's view of the coach. */
  hydrate: (input: {
    gender: CoachGender;
    tone: CoachTone;
    name: string;
    avatarUrl: string;
    dailyCommand: string;
    isConfigured: boolean;
  } | null) => void;
  reset: () => void;
}

const initial = {
  gender: null,
  tone: null,
  name: null,
  avatarUrl: null,
  dailyCommand: null,
  isConfigured: false,
};

export const useCoachStore = create<CoachState>()(
  persist(
    (set) => ({
      ...initial,
      configure: ({ gender, tone, name, avatarUrl, dailyCommand }) => {
        set({ gender, tone, name, avatarUrl, dailyCommand, isConfigured: true });
        syncFireAndForget(() =>
          api.me.updateCoach({ gender, tone, name, avatarUrl, dailyCommand, isConfigured: true }),
        );
      },
      setDailyCommand: (dailyCommand) => {
        set({ dailyCommand });
        syncFireAndForget(() => api.me.updateCoach({ dailyCommand }));
      },
      setName: (name) => {
        set({ name });
        syncFireAndForget(() => api.me.updateCoach({ name }));
      },
      setGender: (gender) => {
        set({ gender });
        syncFireAndForget(() => api.me.updateCoach({ gender }));
      },
      setTone: (tone) => {
        set({ tone });
        syncFireAndForget(() => api.me.updateCoach({ tone }));
      },
      hydrate: (input) =>
        input
          ? set({
              gender: input.gender,
              tone: input.tone,
              name: input.name,
              avatarUrl: input.avatarUrl,
              dailyCommand: input.dailyCommand,
              isConfigured: input.isConfigured,
            })
          : set({ ...initial }),
      reset: () => set({ ...initial }),
    }),
    {
      name: 'llura.coach',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
